import React, { forwardRef } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { shouldNotForwardProps } from '@polestar/component-warehouse-react/helpers/shouldForwardProps';
import { CrossAxisAlignments, MainAxisAlignments, } from '@polestar/component-warehouse-react/tokens/alignments/Alignments';
import useDimensionsCallback from '@polestar/component-warehouse-react/hooks/useDimensions';
import { useSSRSafeId } from '@polestar/component-warehouse-react/contexts/ssr';
const SIZER_FONT = 100;
const KnockoutText = forwardRef(({ children, inverted = false, foregroundColor, background, mainAxisAlignment = MainAxisAlignments.center, crossAxisAlignment = CrossAxisAlignments.center, offset = [0, 0], ...props }, ref) => {
    const memoizedId = useSSRSafeId('knockout-text', props.id);
    const [callbackRef, containerSize] = useDimensionsCallback();
    const [sizeRef, baseDimensions] = useDimensionsCallback({
        width: 1,
        height: 1,
    });
    const baseRatio = baseDimensions.width / baseDimensions.height;
    const containerRatio = containerSize.width / containerSize.height;
    // Container narrower then knockout
    // Default assume we want to span full width, or orientation has been set to horizontal
    let fontSize = (containerSize.width / baseDimensions.width) * SIZER_FONT;
    // Container wider then knockout
    // Span full height, because we can't do full width
    if (containerRatio > baseRatio) {
        fontSize = (containerSize.height / baseDimensions.height) * SIZER_FONT;
    }
    const show = (containerSize.width > 0 || containerSize.height > 0) &&
        (baseDimensions.width > 1 || baseDimensions.height > 1);
    let textAnchor = 'start';
    let dominantBaseline = 'hanging';
    let x = 0;
    let y = 0;
    if (mainAxisAlignment === MainAxisAlignments.center) {
        textAnchor = 'middle';
        x = 50;
    }
    else if (mainAxisAlignment === MainAxisAlignments.end) {
        textAnchor = 'end';
        x = 100;
    }
    if (crossAxisAlignment === CrossAxisAlignments.center) {
        dominantBaseline = 'central';
        y = 50;
    }
    else if (crossAxisAlignment === CrossAxisAlignments.end) {
        dominantBaseline = 'text-after-edge';
        y = 100;
    }
    const [offsetX = 0, offsetY = 0] = offset;
    x += offsetX;
    y += offsetY;
    return (React.createElement(Container, { ref: ref, foregroundColor: foregroundColor, ...props },
        React.createElement(SizeContainer, { ref: callbackRef }),
        React.createElement(Sizer, { ref: sizeRef, "aria-hidden": true }, children),
        show && (React.createElement(React.Fragment, null,
            React.createElement(Inner, { fontSize: fontSize, "aria-hidden": true }, children),
            React.createElement("svg", { key: children.toString(), viewBox: `0 0 ${containerSize.width} ${containerSize.height}` },
                React.createElement("rect", { width: "110%", height: "110%", fill: foregroundColor, x: "-5%", y: "-5%", mask: `url(#${memoizedId})` }),
                React.createElement("mask", { id: memoizedId },
                    React.createElement("rect", { width: "110%", height: "110%", fill: inverted ? '#000' : '#FFF', x: "-5%", y: "-5%" }),
                    React.createElement("text", { x: `${x}%`, y: `${y}%`, fill: !inverted ? '#000' : '#FFF', textAnchor: textAnchor, dominantBaseline: dominantBaseline, fontSize: fontSize, letterSpacing: `${fontSize * -0.0025}rem` }, children))),
            !!background && React.createElement(Background, null, background)))));
});
KnockoutText.displayName = 'KnockoutText';
export default KnockoutText;
const Container = styled.div(({ foregroundColor }) => css `
    position: relative;
    overflow: hidden;
    background: ${foregroundColor};
    width: 100%;
    height: 100%;
    flex: 1;

    svg {
      position: absolute;
      inset-block-start: 0;
      inset-inline-start: 0;
      z-index: 2;
      width: 100%;
      height: 100%;
    }
  `);
Container.displayName = `Container`;
const SizeContainer = styled.div `
  position: absolute;
  width: 100%;
  height: 100%;
  visibility: hidden;
  pointer-events: none;
`;
const Background = styled.div `
  opacity: 1;
  display: block;
  position: absolute;
  z-index: 1;
  inset-block-start: 0;
  inset-inline-end: 0;
  inset-block-end: 0;
  inset-inline-start: 0;
  overflow: hidden;

  > * {
    width: 100%;
    height: 100%;
  }
`;
const Sizer = styled.div `
  position: fixed;
  font-size: ${SIZER_FONT}px;
  line-height: ${SIZER_FONT}px;
  letter-spacing: ${-0.25}rem;
  white-space: nowrap;
  inset-inline-start: 100vw;
  visibility: hidden;
  pointer-events: none;
`;
Sizer.displayName = `Sizer`;
const Inner = styled('div', shouldNotForwardProps(true, 'fontSize'))(({ fontSize }) => css `
    visibility: hidden;
    position: absolute;
    z-index: 3;
    white-space: nowrap;
    font-size: ${fontSize || 10}px;
    line-height: ${fontSize}px;
    letter-spacing: ${fontSize * -0.0025}rem;
    pointer-events: none;
  `);
Inner.displayName = `Inner`;
